import React from 'react';

function lazyImportWithRecovery<T>(importer: () => Promise<T>, key: string): Promise<T> {
  return importer()
    .then((m) => {
      if (!m) {
        throw new Error(`[InTouch] Module chunk is undefined for ${key}`);
      }
      return m;
    })
    .catch((error) => {
      const message = error instanceof Error ? error.message : String(error);
      const staleChunk =
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('Unexpected token') ||
        message.includes('MIME type') ||
        message.includes('module script') ||
        message.includes('Cannot read properties of undefined') ||
        message.includes('Module chunk is undefined');

      if (staleChunk && typeof window !== 'undefined') {
        const marker = `lazy-reload-once:${key}`;
        const lastReload = sessionStorage.getItem(marker);
        const now = Date.now();

        if (!lastReload || now - Number(lastReload) > 10000) {
          sessionStorage.setItem(marker, String(now));
          console.warn('[InTouch] Lazy route chunk failed to load. Reloading once to recover stale assets.', {
            key,
            message,
          });
          window.location.reload();
          return new Promise<T>(() => {
            // Prevent rendering from continuing while the reload is in progress.
          });
        }
      }

      throw error;
    });
}

// Route-level code splitting: keep initial bundle small and load pages on demand.
const Home = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/Home'), 'home-page').then(m => ({ default: m?.Home || m?.default }))
);
const HotelPage = React.lazy(() => import('./pages/hotel'));
const NewsPage = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/News'), 'news-page').then(m => ({ default: m?.NewsPage || m?.default }))
);
const FeedPage = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/feed'), 'feed-page').then(m => ({ default: m?.FeedPage || m?.default }))
);
const PostPage = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/post/PostPage'), 'post-page').then(m => ({ default: m?.PostPage || m?.default }))
);
const Profile = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/Profile'), 'profile-page').then(m => ({ default: m?.Profile || m?.default }))
);
const UserProfilePage = React.lazy(() => import('./pages/profile/UserProfilePage').then(m => ({ default: m.UserProfilePage })));
const UserPetsPage = React.lazy(() => import('./pages/profile/UserPetsPage').then(m => ({ default: m.UserPetsPage })));
const PetProfilePage = React.lazy(() => import('./pages/profile/PetProfilePage').then(m => ({ default: m.PetProfilePage })));
const PetsPage = React.lazy(() => import('./pages/Pets').then(m => ({ default: m.PetsPage })));
const Messages = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/Messages'), 'messages-page').then(m => ({ default: m?.Messages || m?.default }))
);
const Marketplace = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/Marketplace'), 'marketplace-page').then(m => ({ default: m?.Marketplace || m?.default }))
);
const ProductDetail = React.lazy(() => import('./pages/ProductDetail').then(m => ({ default: m.ProductDetail })));
const Learning = React.lazy(() => import('./pages/Learning').then(m => ({ default: m.Learning })));
const Games = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/Games'), 'games-page').then((m) => ({ default: m?.Games || m?.default }))
);
const Music = React.lazy(() => import('./pages/Music'));
const Videos = React.lazy(() => import('./pages/Videos'));
const Food = React.lazy(() => import('./pages/Food').then(m => ({ default: m.Food })));
const Settings = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/Settings'), 'settings-page').then(m => ({ default: m?.Settings || m?.default }))
);
const Exchange = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/exchange'), 'exchange-page').then((m) => ({ default: m?.Exchange || m?.default }))
);
const MiningPage = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/mining'), 'mining-page').then((m) => ({ default: m?.MiningPage || m?.default }))
);
const StakingPage = React.lazy(() => import('./pages/staking').then(m => ({ default: m.StakingPage })));
const EventsPage = React.lazy(() => import('./pages/events').then(m => ({ default: m.EventsPage })));
const Groups = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/Groups'), 'groups-page').then((m) => ({ default: m.Groups }))
);
const PagesAdvanced = React.lazy(() => import('./pages/pages').then(m => ({ default: m.PagesPage })));
const PageDetail = React.lazy(() => import('./pages/PageDetail'));
const NotificationsPage = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/NotificationsPage'), 'notifications-page').then(m => ({ default: m?.NotificationsPage || m?.default }))
);
const Bookings = React.lazy(() => import('./pages/Bookings').then(m => ({ default: m.Bookings })));
const Charity = React.lazy(() => import('./pages/Charity').then(m => ({ default: m.Charity })));
const Crowdfunding = React.lazy(() => import('./pages/Crowdfunding').then(m => ({ default: m.Crowdfunding })));
const Classifieds = React.lazy(() => import('./pages/Classifieds').then(m => ({ default: m.Classifieds })));
const Coupons = React.lazy(() => import('./pages/Coupons').then(m => ({ default: m.Coupons })));
const Boost = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/Boost'), 'boost-page').then((m) => ({ default: m.Boost }))
);
const Rentals = React.lazy(() => import('./pages/Rentals').then(m => ({ default: m.Rentals })));
const RentalDetail = React.lazy(() => import('./pages/RentalDetail').then(m => ({ default: m.RentalDetail })));
const ListRental = React.lazy(() => import('./pages/ListRental').then(m => ({ default: m.ListRental })));
const Recipes = React.lazy(() => import('./pages/Recipes').then(m => ({ default: m.Recipes })));
const Rewards = React.lazy(() => import('./pages/Rewards').then(m => ({ default: m.Rewards })));
const KYCPage = React.lazy(() => import('./pages/kyc').then(m => ({ default: m.KYCPage })));
const TransportationPage = React.lazy(() => import('./pages/transportation').then(m => ({ default: m.TransportationPage })));
const AirlineTickets = React.lazy(() => import('./pages/transportation/airline').then(m => ({ default: m.AirlineTickets })));
const TrainTickets = React.lazy(() => import('./pages/transportation/train').then(m => ({ default: m.TrainTickets })));
const BusTickets = React.lazy(() => import('./pages/transportation/bus').then(m => ({ default: m.BusTickets })));
const FerryTickets = React.lazy(() => import('./pages/transportation/ferry').then(m => ({ default: m.FerryTickets })));
const TaxiService = React.lazy(() => import('./pages/transportation/taxi').then(m => ({ default: m.TaxiService })));
const AdminDashboard = React.lazy(() => import('./pages/admin/dashboard').then(m => ({ default: m.AdminDashboard })));
const SuperAdminDashboard = React.lazy(() => import('./pages/admin/super-admin-dashboard').then(m => ({ default: m.SuperAdminDashboard })));
const TelemetryDashboardPage = React.lazy(() => import('./pages/admin/telemetry-dashboard').then(m => ({ default: m.TelemetryDashboardPage })));
const SuperAdminDebugPanel = React.lazy(() => import('./components/admin/super-admin-debug-simple').then(m => ({ default: m.SuperAdminDebugPanelSimple })));
const MiningDashboard = React.lazy(() => import('./pages/admin/mining/dashboard').then(m => ({ default: m.MiningDashboard })));
const SellerDashboard = React.lazy(() => import('./pages/marketplace/seller/dashboard').then(m => ({ default: m.SellerDashboard })));
const SellerProducts = React.lazy(() => import('./pages/marketplace/seller/products').then(m => ({ default: m.SellerProducts })));
const SellerOrders = React.lazy(() => import('./pages/seller/orders').then(m => ({ default: m.SellerOrders })));
const SellerAnalytics = React.lazy(() => import('./pages/seller/analytics').then(m => ({ default: m.SellerAnalytics })));
const PrivacySettingsPage = React.lazy(() => import('./pages/settings/privacy').then(m => ({ default: m.PrivacySettingsPage })));
const HelpCenter = React.lazy(() => import('./pages/help').then(m => ({ default: m.HelpCenter })));
const Documentation = React.lazy(() => import('./pages/docs').then(m => ({ default: m.Documentation })));
const FreelancePage = React.lazy(() => import('./pages/freelance').then(m => ({ default: m.FreelancePage })));
const GroceriesPage = React.lazy(() => import('./pages/groceries').then(m => ({ default: m.GroceriesPage })));
const TicketsPage = React.lazy(() => import('./pages/tickets').then(m => ({ default: m.TicketsPage })));
const FriendsPage = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/friends'), 'friends-page').then(m => ({ default: m?.FriendsPage || m?.default }))
);
const Wallet = React.lazy(() =>
  lazyImportWithRecovery(() => import('./pages/Wallet'), 'wallet-page').then(m => ({ default: m?.Wallet || m?.default }))
);
const CreditCard = React.lazy(() => import('./pages/credit-card'));
const CryptoAtlasPage = React.lazy(() => import('./pages/crypto-atlas').then(m => ({ default: m.CryptoAtlasPage })));
const CryptoAtlasDetailPage = React.lazy(() => import('./pages/crypto-atlas/detail').then(m => ({ default: m.CryptoAtlasDetailPage })));
const CreateCampaignPage = React.lazy(() => import('./pages/create/campaign').then(m => ({ default: m.CreateCampaignPage })));
const CreateGroupPage = React.lazy(() => import('./pages/create/group').then(m => ({ default: m.CreateGroupPage })));
const CreatePostPage = React.lazy(() => import('./pages/create/post').then(m => ({ default: m.CreatePostPage })));
const BookingDetailsPage = React.lazy(() => import('./pages/booking/details').then(m => ({ default: m.BookingDetailsPage })));
const ProductDetailsPage = React.lazy(() => import('./pages/product/details').then(m => ({ default: m.ProductDetailsPage })));
const EventDetailsPage = React.lazy(() => import('./pages/event/details').then(m => ({ default: m.EventDetailsPage })));
const CreateHotelListingPage = React.lazy(() => import('./pages/create/hotel').then(m => ({ default: m.CreateHotelListingPage })));
const CreateApartmentListingPage = React.lazy(() => import('./pages/create/apartment').then(m => ({ default: m.CreateApartmentListingPage })));
const CreateRecipePage = React.lazy(() => import('./pages/create/recipe').then(m => ({ default: m.CreateRecipePage })));
const MyProducts = React.lazy(() => import('./pages/MyProducts').then(m => ({ default: m.MyProducts })));
const CallHistory = React.lazy(() => import('./components/calls/CallHistory').then(m => ({ default: m.CallHistory })));
const MediaUploaderShowcase = React.lazy(() => import('./pages/MediaDemo').then(m => ({ default: m.MediaUploaderShowcase })));
const MediaIntegrationShowcase = React.lazy(() => import('./pages/MediaIntegrationShowcase').then(m => ({ default: m.MediaIntegrationShowcase })));
const LanguageTestPage = React.lazy(() => import('./pages/LanguageTestPage').then(m => ({ default: m.LanguageTestPage })));
const AIHub = React.lazy(() => import('./components/ai').then(m => ({ default: m.AIHub })));
const AccessibilityPage = React.lazy(() => import('./pages/accessibility').then(m => ({ default: m.AccessibilityPage })));

// Game Components
const SpaceInvadersGame = React.lazy(() =>
  lazyImportWithRecovery(() => import('./components/games/space-invaders/SpaceInvadersGame'), 'game-space-invaders')
    .then((m) => ({ default: m.SpaceInvadersGame }))
);
const TetrisNeoGame = React.lazy(() =>
  lazyImportWithRecovery(() => import('./components/games/tetris/TetrisNeoGame'), 'game-tetris-neo')
    .then((m) => ({ default: m.TetrisNeoGame }))
);
const SnakeGameAdvanced = React.lazy(() =>
  lazyImportWithRecovery(() => import('./components/games/snake/SnakeGameAdvanced'), 'game-snake-advanced')
    .then((m) => ({ default: m.SnakeGameAdvanced }))
);
const Game2048 = React.lazy(() =>
  lazyImportWithRecovery(() => import('./components/games/2048/2048Game'), 'game-2048')
    .then((m) => ({ default: m.Game2048 }))
);
const MemoryGame = React.lazy(() =>
  lazyImportWithRecovery(() => import('./components/games/memory/MemoryGame'), 'game-memory')
    .then((m) => ({ default: m.MemoryGame }))
);
const MinesweeperGame = React.lazy(() =>
  lazyImportWithRecovery(() => import('./components/games/minesweeper/MinesweeperGame'), 'game-minesweeper')
    .then((m) => ({ default: m.MinesweeperGame }))
);
const BreakoutGame = React.lazy(() =>
  lazyImportWithRecovery(() => import('./components/games/breakout/BreakoutGame'), 'game-breakout')
    .then((m) => ({ default: m.BreakoutGame }))
);
const ReflexArenaGame = React.lazy(() =>
  lazyImportWithRecovery(() => import('./components/games/reflex-arena/ReflexArenaGame'), 'game-reflex-arena')
    .then((m) => ({ default: m.ReflexArenaGame }))
);
const CipherBreakerGame = React.lazy(() =>
  lazyImportWithRecovery(() => import('./components/games/cipher-breaker/CipherBreakerGame'), 'game-cipher-breaker')
    .then((m) => ({ default: m.CipherBreakerGame }))
);

const likelyRoutePreloaders: Array<() => Promise<unknown>> = [
  () => import('./pages/feed'),
  () => import('./pages/Profile'),
  () => import('./pages/Messages'),
  () => import('./pages/NotificationsPage'),
  () => import('./pages/News'),
  () => import('./pages/Marketplace'),
  () => lazyImportWithRecovery(() => import('./pages/mining'), 'mining-page-preload'),
  () => import('./pages/Wallet'),
];

const routePrefetchers: Record<string, () => Promise<unknown>> = {
  '/': () => import('./pages/Home'),
  '/feed': () => import('./pages/feed'),
  '/profile': () => import('./pages/Profile'),
  '/messages': () => import('./pages/Messages'),
  '/notifications': () => import('./pages/NotificationsPage'),
  '/marketplace': () => import('./pages/Marketplace'),
  '/wallet': () => import('./pages/Wallet'),
  '/events': () => import('./pages/events'),
  '/news': () => import('./pages/News'),
  '/admin/telemetry': () => import('./pages/admin/telemetry-dashboard'),
  '/games': () => lazyImportWithRecovery(() => import('./pages/Games'), 'games-page-prefetch'),
  '/exchange': () => lazyImportWithRecovery(() => import('./pages/exchange'), 'exchange-page-prefetch'),
  '/mining': () => lazyImportWithRecovery(() => import('./pages/mining'), 'mining-page-prefetch-single'),
};

const prefetchedRouteKeys = new Set<string>();

function canPrefetchRoutes(): boolean {
  const conn = (navigator as any).connection;
  if (conn?.saveData) return false;
  if (typeof conn?.effectiveType === 'string' && /2g|slow-2g/i.test(conn.effectiveType)) {
    return false;
  }
  return true;
}

function resolvePrefetchKey(pathname: string): string | null {
  const normalized = pathname.split('?')[0].split('#')[0] || '/';
  const exact = Object.keys(routePrefetchers).find((key) => key === normalized);
  if (exact) return exact;

  const match = Object.keys(routePrefetchers).find((key) => key !== '/' && normalized.startsWith(`${key}/`));
  return match || null;
}

export function prefetchRoute(pathname: string): void {
  if (!pathname || !canPrefetchRoutes()) return;

  const key = resolvePrefetchKey(pathname);
  if (!key || prefetchedRouteKeys.has(key)) return;

  prefetchedRouteKeys.add(key);

  void routePrefetchers[key]().catch(() => {
    prefetchedRouteKeys.delete(key);
  });
}

export function preloadLikelyRoutes(): void {
  if (!canPrefetchRoutes()) return;

  const conn = (navigator as any).connection;

  const effectiveType = typeof conn?.effectiveType === 'string' ? conn.effectiveType.toLowerCase() : '';
  const deviceMemory = Number((navigator as any).deviceMemory || 0);

  const maxPreloads = /3g/i.test(effectiveType)
    ? 1
    : deviceMemory > 0 && deviceMemory <= 4
      ? 3
      : 5;

  likelyRoutePreloaders.slice(0, maxPreloads).forEach((loader) => {
    void loader().catch(() => undefined);
  });

  // Warm route bundles that are commonly visited from landing context.
  ['/feed', '/messages', '/notifications', '/wallet'].slice(0, Math.min(3, maxPreloads)).forEach((path) => {
    prefetchRoute(path);
  });
}

export const routes = [
  { path: '/', component: Home },
  { path: '/login', component: Home },
  { path: '/signup', component: Home },
  { path: '/auth/login', component: Home },
  { path: '/auth/signup', component: Home },
  { path: '/news', component: NewsPage },
  { path: '/feed', component: FeedPage },
  { path: '/post/:postId', component: PostPage },
  { path: '/pets/:petId', component: PetProfilePage },
  { path: '/pets', component: PetsPage },
  { path: '/profile/:username/pets/:petId', component: PetProfilePage },
  { path: '/profile/:username/pets', component: UserPetsPage },
  { path: '/profile/:username', component: UserProfilePage },
  { path: '/profile', component: Profile },
  { path: '/friends', component: FriendsPage },
  { path: '/messages', component: Messages },
  { path: '/marketplace/:productId', component: ProductDetail },
  { path: '/marketplace/*', component: Marketplace },
  { path: '/learning', component: Learning },
  { path: '/games', component: Games },
  { path: '/games/space-invaders', component: SpaceInvadersGame },
  { path: '/games/tetris', component: TetrisNeoGame },
  { path: '/games/snake', component: SnakeGameAdvanced },
  { path: '/games/2048', component: Game2048 },
  { path: '/games/memory', component: MemoryGame },
  { path: '/games/minesweeper', component: MinesweeperGame },
  { path: '/games/breakout', component: BreakoutGame },
  { path: '/games/reflex-arena', component: ReflexArenaGame },
  { path: '/games/cipher-breaker', component: CipherBreakerGame },
  { path: '/music', component: Music },
  { path: '/videos', component: Videos },
  { path: '/food', component: Food },
  { path: '/settings', component: Settings },
  { path: '/accessibility', component: AccessibilityPage },
  { path: '/ai/*', component: AIHub },
  { path: '/wallet', component: Wallet },
  { path: '/credit-card', component: CreditCard },
  { path: '/crypto-atlas', component: CryptoAtlasPage },
  { path: '/crypto-atlas/:provider/:coinId', component: CryptoAtlasDetailPage },
  { path: '/exchange', component: Exchange },
  { path: '/mining', component: MiningPage },
  { path: '/staking', component: StakingPage },
  { path: '/events', component: EventsPage },
  { path: '/groups/*', component: Groups },
  { path: '/pages/:pageId', component: PageDetail },
  { path: '/pages/*', component: PagesAdvanced },
  { path: '/notifications', component: NotificationsPage },
  { path: '/charity', component: Charity },
  { path: '/crowdfunding', component: Crowdfunding },
  { path: '/bookings', component: Bookings },
  { path: '/rentals/new', component: ListRental },
  { path: '/rentals/:id', component: RentalDetail },
  { path: '/rentals', component: Rentals },
  { path: '/classifieds', component: Classifieds },
  { path: '/coupons', component: Coupons },
  { path: '/boost', component: Boost },
  { path: '/recipes', component: Recipes },
  { path: '/rewards', component: Rewards },
  { path: '/kyc', component: KYCPage },
  { path: '/transportation', component: TransportationPage },
  { path: '/transportation/airline', component: AirlineTickets },
  { path: '/transportation/train', component: TrainTickets },
  { path: '/transportation/bus', component: BusTickets },
  { path: '/transportation/ferry', component: FerryTickets },
  { path: '/transportation/taxi', component: TaxiService },
  { path: '/admin/super-admin-dashboard', component: SuperAdminDashboard },
  { path: '/admin/telemetry', component: TelemetryDashboardPage },
  { path: '/admin/debug', component: SuperAdminDebugPanel },
  { path: '/admin/dashboard', component: AdminDashboard },
  { path: '/admin/mining', component: MiningDashboard },
  { path: '/seller/dashboard', component: SellerDashboard },
  { path: '/seller/products', component: SellerProducts },
  { path: '/seller/orders', component: SellerOrders },
  { path: '/seller/analytics', component: SellerAnalytics },
  { path: '/privacy', component: PrivacySettingsPage },
  { path: '/settings/privacy', component: PrivacySettingsPage },
  { path: '/help', component: HelpCenter },
  { path: '/docs', component: Documentation },
  { path: '/freelance/*', component: FreelancePage },
  { path: '/groceries', component: GroceriesPage },
  { path: '/hotel/:hotelId', component: HotelPage },
  { path: '/tickets', component: TicketsPage },
  { path: '/create/campaign', component: CreateCampaignPage },
  { path: '/create/group', component: CreateGroupPage },
  { path: '/create/post', component: CreatePostPage },
  { path: '/booking/:id', component: BookingDetailsPage },
  { path: '/product/:id', component: ProductDetailsPage },
  { path: '/event/:id', component: EventDetailsPage },
  { path: '/create/hotel', component: CreateHotelListingPage },
  { path: '/create/apartment', component: CreateApartmentListingPage },
  { path: '/create/recipe', component: CreateRecipePage },
  { path: '/my-products', component: MyProducts },
  { path: '/calls', component: CallHistory },
  { path: '/media-demo', component: MediaUploaderShowcase },
  { path: '/media-integration', component: MediaIntegrationShowcase },
  { path: '/language-test', component: LanguageTestPage },
  { path: '*', component: Home } // Catch-all route for undefined paths
];